import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Input, Radio, Select, Space, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { Dialog as ZhDialog, MessageBox, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/views/stores";
import RequiredMark from "@/views/components/RequiredMark";

export interface SearchFoundRow {
    id: number;
    keyword?: string;
    type?: number | string;
    businessId?: string;
    businessIdTwo?: string;
    clickNum?: number;
    isTop?: number | string;
    createdTime?: string;
    editTime?: string;
}

export interface SearchFoundQuery {
    page: number;
    size: number;
}

export interface SearchFoundFormValues {
    keyword: string;
    type: number;
    businessId: string;
    businessIdTwo: string;
}

export interface SearchFoundSelectorOption {
    id: number | string;
    label: string;
}

export interface SearchFoundPageProps {
    loadSearches?: (query: SearchFoundQuery) => Promise<{ list?: SearchFoundRow[]; total?: number }>;
    loadDetail?: (id: number) => Promise<SearchFoundFormValues & { id: number }>;
    create?: (values: SearchFoundFormValues) => Promise<unknown>;
    update?: (id: number, values: SearchFoundFormValues) => Promise<unknown>;
    remove?: (id: number) => Promise<unknown>;
    top?: (id: number) => Promise<unknown>;
    loadProductOptions?: (keyword: string) => Promise<SearchFoundSelectorOption[]>;
    permissions?: string[];
}

const defaultQuery: SearchFoundQuery = { page: 1, size: 10 };
const typeLabels: Record<number, string> = {
    1: "搜索词",
    2: "商品",
    3: "砂价",
    4: "运价",
    5: "广告页",
    6: "资讯"
};
const typeOptions = Object.entries(typeLabels).map(([value, label]) => ({ value: Number(value), label }));
const deleteConfirmOptions = {
    closeOnClickModal: false,
    showClose: true,
    confirmButtonText: "确认"
};

/** 复用 Vue 搜索发现分页接口。 */
export async function loadSearchFound(query: SearchFoundQuery): Promise<{ list?: SearchFoundRow[]; total?: number }> {
    const response = await request.service.get("/admin/search/paging", { params: query });
    return response.data;
}

/** 复用 Vue 搜索发现详情接口。 */
export async function loadSearchFoundDetail(id: number): Promise<SearchFoundFormValues & { id: number }> {
    const response = await request.service.get(`/admin/search/${id}`);
    return response.data;
}

/** 复用 Vue 搜索发现新增接口。 */
export async function createSearchFound(values: SearchFoundFormValues): Promise<unknown> {
    const response = await request.service.post("/admin/search", values);
    return response.data;
}

/** 复用 Vue 搜索发现编辑接口。 */
export async function updateSearchFound(id: number, values: SearchFoundFormValues): Promise<unknown> {
    const response = await request.service.put(`/admin/search/${id}`, values);
    return response.data;
}

/** 复用 Vue 搜索发现删除接口。 */
export async function deleteSearchFound(id: number): Promise<unknown> {
    const response = await request.service.delete(`/admin/search/${id}`);
    return response.data;
}

/** 复用 Vue 搜索发现置顶/取消置顶接口。 */
export async function topSearchFound(id: number): Promise<unknown> {
    const response = await request.service.put(`/admin/search/top/${id}`);
    return response.data;
}

interface SearchFoundProductSelectorItem {
    id: number | string;
    title?: string;
    productTypeName?: string;
    productTypeSpec?: string | number;
}

/** 复用 Vue 商品远程选择接口，并保持页码、条数及展示文案契约。 */
export async function loadSearchFoundProductOptions(keyword: string): Promise<SearchFoundSelectorOption[]> {
    const response = await request.service.get<{ list?: SearchFoundProductSelectorItem[] }>(
        "/admin/product-consignment/paging-selector",
        { params: { keyword, page: 1, size: 50 } }
    );
    return (response.data.list || []).map(item => ({
        id: item.id,
        label: [item.title, item.productTypeName && `${item.productTypeName}(${item.productTypeSpec ?? ""})`, item.id]
            .filter(Boolean)
            .join(" | ")
    }));
}

function formatDate(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

function emptyForm(): SearchFoundFormValues {
    return { keyword: "", type: 1, businessId: "", businessIdTwo: "" };
}

function SearchFoundEditor({
    id,
    open,
    loadDetail,
    create,
    update,
    loadProductOptions,
    onClose,
    onSaved
}: {
    id?: number;
    open: boolean;
    loadDetail: (id: number) => Promise<SearchFoundFormValues & { id: number }>;
    create: (values: SearchFoundFormValues) => Promise<unknown>;
    update: (id: number, values: SearchFoundFormValues) => Promise<unknown>;
    loadProductOptions: (keyword: string) => Promise<SearchFoundSelectorOption[]>;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [form, setForm] = useState<SearchFoundFormValues>(emptyForm);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [productOptions, setProductOptions] = useState<SearchFoundSelectorOption[]>([]);

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setErrorMessage(null);
        if (id == null) {
            setForm(emptyForm());
            setProductOptions([]);
            return;
        }
        setLoading(true);
        void loadDetail(id)
            .then(detail => {
                setForm({
                    keyword: detail.keyword || "",
                    type: Number(detail.type),
                    businessId: detail.businessId || "",
                    businessIdTwo: detail.businessIdTwo || ""
                });
                // Vue 编辑态会按已有商品 ID 回填候选，保证选择器展示可读名称。
                if (Number(detail.type) === 2 && detail.businessId) {
                    void loadProductOptions(detail.businessId)
                        .then(options => setProductOptions(options))
                        .catch(() => setErrorMessage("商品搜索失败"));
                } else {
                    setProductOptions([]);
                }
            })
            .catch(() => setErrorMessage("搜索发现详情加载失败"))
            .finally(() => setLoading(false));
    }, [id, loadDetail, loadProductOptions, open]);

    const updateForm = <K extends keyof SearchFoundFormValues>(key: K, value: SearchFoundFormValues[K]) => {
        setForm(current => ({ ...current, [key]: value }));
        setErrors(current => ({ ...current, [String(key)]: "" }));
    };

    const validate = () => {
        const nextErrors: Record<string, string> = {};
        if (!form.keyword.trim()) nextErrors.keyword = "请输入关键词名称";
        if (!form.type) nextErrors.type = "请选择跳转类型";
        if (form.type >= 2 && form.type <= 6 && !form.businessId) nextErrors.businessId = "请输入业务值";
        if (form.type === 5 && !form.businessIdTwo) nextErrors.businessIdTwo = "请输入用户PC广告页";
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    /** 按 Vue remote-method 查询商品候选，清空关键词时不发送请求。 */
    const searchProducts = async (keyword: string) => {
        if (!keyword.trim()) {
            setProductOptions([]);
            return;
        }
        try {
            setProductOptions(await loadProductOptions(keyword));
        } catch {
            setProductOptions([]);
            setErrorMessage("商品搜索失败");
        }
    };

    const submit = async () => {
        if (!validate()) return;
        try {
            if (id == null) await create(form);
            else await update(id, form);
            onSaved();
            onClose();
        } catch {
            setErrorMessage("搜索发现保存失败");
        }
    };

    const renderBusinessField = () => {
        if (form.type === 1) return null;
        if (form.type === 5) {
            return (
                <>
                    <label>
                        <span>
                            <RequiredMark />
                            小程序广告页
                        </span>
                        <Select
                            allowClear
                            aria-label="小程序广告页"
                            className="w-full"
                            options={form.businessId ? [{ label: form.businessId, value: form.businessId }] : []}
                            placeholder="请搜索小程序广告页标题或编号选择"
                            showSearch
                            value={form.businessId || undefined}
                            onChange={value => updateForm("businessId", String(value || ""))}
                        />
                        {errors.businessId ? <span role="alert">{errors.businessId}</span> : null}
                    </label>
                    <label>
                        <span>
                            <RequiredMark />
                            用户PC广告页
                        </span>
                        <Select
                            allowClear
                            aria-label="用户PC广告页"
                            className="w-full"
                            options={
                                form.businessIdTwo ? [{ label: form.businessIdTwo, value: form.businessIdTwo }] : []
                            }
                            placeholder="请搜索用户PC广告页标题或编号选择"
                            showSearch
                            value={form.businessIdTwo || undefined}
                            onChange={value => updateForm("businessIdTwo", String(value || ""))}
                        />
                        {errors.businessIdTwo ? <span role="alert">{errors.businessIdTwo}</span> : null}
                    </label>
                </>
            );
        }
        const labels: Record<number, string> = { 2: "商品", 3: "砂石指数", 4: "运价指数", 6: "资讯" };
        if (form.type === 2) {
            return (
                <>
                    <label>
                        <span>
                            <RequiredMark />
                            商品
                        </span>
                        <Select
                            allowClear
                            aria-label="商品搜索"
                            className="w-full"
                            filterOption={false}
                            options={productOptions.map(option => ({ label: option.label, value: String(option.id) }))}
                            placeholder="请搜索商品名称或商品id选择"
                            showSearch
                            value={form.businessId || undefined}
                            onChange={value => updateForm("businessId", String(value))}
                            onClear={() => updateForm("businessId", "")}
                            onSearch={value => void searchProducts(value)}
                        />
                        {errors.businessId ? <span role="alert">{errors.businessId}</span> : null}
                    </label>
                </>
            );
        }
        const placeholders: Record<number, string> = {
            3: "请搜索品类名称或品类代码选择",
            4: "请搜索码头名称或航线代码选择",
            6: "请搜索资讯标题"
        };
        return (
            <label>
                <span>
                    <RequiredMark />
                    {labels[form.type] || "业务值"}
                </span>
                <Select
                    allowClear
                    aria-label={labels[form.type] || "业务值"}
                    className="w-full"
                    options={form.businessId ? [{ label: form.businessId, value: form.businessId }] : []}
                    placeholder={placeholders[form.type]}
                    showSearch
                    value={form.businessId || undefined}
                    onChange={value => updateForm("businessId", String(value || ""))}
                />
                {errors.businessId ? <span role="alert">{errors.businessId}</span> : null}
            </label>
        );
    };

    return (
        <ZhDialog
            destroyOnClose
            open={open}
            title={id == null ? "新增关键词" : "编辑关键词"}
            width={650}
            footer={null}
            onCancel={onClose}
        >
            {loading ? <div>加载中</div> : null}
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            <Space direction="vertical" style={{ width: "100%" }}>
                <label>
                    <span>
                        <RequiredMark />
                        关键词名称
                    </span>
                    <Input
                        aria-label="关键词名称"
                        maxLength={10}
                        value={form.keyword}
                        onChange={event => updateForm("keyword", event.target.value)}
                    />
                    {errors.keyword ? <span role="alert">{errors.keyword}</span> : null}
                </label>
                <label>
                    <span>
                        <RequiredMark />
                        跳转类型
                    </span>
                    <Radio.Group
                        aria-label="跳转类型"
                        options={typeOptions}
                        value={form.type}
                        onChange={event => {
                            updateForm("type", Number(event.target.value));
                            updateForm("businessId", "");
                            updateForm("businessIdTwo", "");
                        }}
                    />
                    {errors.type ? <span role="alert">{errors.type}</span> : null}
                </label>
                {renderBusinessField()}
                <Space>
                    <Button onClick={onClose}>取消</Button>
                    <Button type="primary" onClick={() => void submit()}>
                        保存
                    </Button>
                </Space>
            </Space>
        </ZhDialog>
    );
}

/** 搜索发现配置列表与编辑弹窗，保留 Vue 的置顶状态机和类型分支。 */
export function SearchFoundPage({
    loadSearches = loadSearchFound,
    loadDetail = loadSearchFoundDetail,
    create = createSearchFound,
    update = updateSearchFound,
    remove = deleteSearchFound,
    top = topSearchFound,
    loadProductOptions = loadSearchFoundProductOptions,
    permissions
}: SearchFoundPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions ?? storePermissions;
    const canManage = currentPermissions.includes("ROLE_SEARCH_W");
    const [rows, setRows] = useState<SearchFoundRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState(defaultQuery);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [reloadToken, setReloadToken] = useState(0);
    const [editor, setEditor] = useState<{ open: boolean; id?: number }>({ open: false });

    useEffect(() => {
        let disposed = false;
        void loadSearches(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("搜索发现列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadSearches, query, reloadToken]);

    const runAction = async (action: () => Promise<unknown>) => {
        try {
            await action();
            setReloadToken(value => value + 1);
        } catch {
            setErrorMessage("搜索发现操作失败");
        }
    };

    /** 对齐 Vue confirmable：删除请求必须在用户确认后发送，取消不提示操作失败。 */
    const confirmDelete = async (id: number) => {
        try {
            const result = await MessageBox.confirm("是否删除", "提示", deleteConfirmOptions);
            if (result !== "confirm") return;
            await runAction(() => remove(id));
        } catch {
            // MessageBox 取消/关闭时沿用 Vue 页面静默返回，避免误报操作失败。
        }
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 搜索发现列表的状态与操作插槽。
    const columns: TableColumnProps<SearchFoundRow>[] = [
        { key: "id", label: "编号", width: 120, prop: "id", render: ({ row }) => row.id },
        {
            key: "keyword",
            label: "关键词",
            minWidth: 120,
            prop: "keyword",
            render: ({ row }) => row.keyword || DEFAULT_VALUE
        },
        {
            key: "type",
            label: "跳转类型",
            minWidth: 120,
            prop: "type",
            render: ({ row }) => typeLabels[Number(row.type)] || DEFAULT_VALUE
        },
        {
            key: "clickNum",
            label: "点击量",
            minWidth: 120,
            prop: "clickNum",
            render: ({ row }) => row.clickNum ?? DEFAULT_VALUE
        },
        {
            key: "createdTime",
            label: "创建时间",
            minWidth: 120,
            prop: "createdTime",
            render: ({ row }) => formatDate(row.createdTime)
        },
        {
            key: "editTime",
            label: "更新时间",
            minWidth: 120,
            prop: "editTime",
            render: ({ row }) => formatDate(row.editTime)
        },
        {
            key: "isTop",
            label: "是否置顶",
            minWidth: 120,
            prop: "isTop",
            render: ({ row }) => <Tag>{Number(row.isTop) === 1 ? "是" : "否"}</Tag>
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 160,
            prop: "id",
            render: ({ row }) =>
                !canManage ? null : Number(row.isTop) === 1 ? (
                    <Button
                        aria-label={`取消置顶搜索发现 ${row.id}`}
                        disabled={banButton}
                        type="link"
                        onClick={() => void runAction(() => top(row.id))}
                    >
                        取消置顶
                    </Button>
                ) : (
                    <Space>
                        <Button
                            aria-label={`编辑搜索发现 ${row.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => setEditor({ open: true, id: row.id })}
                        >
                            编辑
                        </Button>
                        <Button
                            aria-label={`删除搜索发现 ${row.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => void confirmDelete(row.id)}
                        >
                            删除
                        </Button>
                        <Button
                            aria-label={`置顶搜索发现 ${row.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => void runAction(() => top(row.id))}
                        >
                            置顶
                        </Button>
                    </Space>
                )
        }
    ];

    return (
        <section aria-label="search-found-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<SearchFoundRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns)}
                    emptyText="暂无数据"
                    pagination={false}
                    rowKey={row => String(row.id)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                搜索发现配置
                            </Typography.Title>
                            <div className="admin-table-filter-form mb-4">
                                {canManage ? (
                                    <Button
                                        className="admin-table-primary-action"
                                        aria-label="新增搜索发现"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => setEditor({ open: true })}
                                    >
                                        新增
                                    </Button>
                                ) : null}
                            </div>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                onChange={(page, size) => setQuery(current => ({ ...current, page, size }))}
                            />
                        </div>
                    }
                />
            </Card>
            <SearchFoundEditor
                create={create}
                id={editor.id}
                loadDetail={loadDetail}
                loadProductOptions={loadProductOptions}
                open={editor.open}
                update={update}
                onClose={() => setEditor({ open: false })}
                onSaved={() => setReloadToken(value => value + 1)}
            />
        </section>
    );
}
